import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  LocalHospitalRounded,
  CheckCircleRounded,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { db } from 'services/firebase';
import { submitCheckIn } from 'services/clinicService';

/**
 * Public clinic check-in landing page (route: /checkin/:clinicId).
 *
 * The clinic name comes from the public `locations` doc. Checking in requires
 * sign-in; the actual write goes through the createCheckInVisit Cloud Function,
 * which records the visit under the patient's auth uid.
 */
const CheckInPage = () => {
  const { clinicId } = useParams();
  const { user, userLoading } = useAuth();

  const [clinicName, setClinicName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [checkingIn, setCheckingIn] = useState(false);
  const [checkInError, setCheckInError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let active = true;
    if (!clinicId) {
      setError('This check-in link is missing a clinic.');
      setLoading(false);
      return undefined;
    }
    db.collection('locations')
      .doc(clinicId)
      .get()
      .then((snap) => {
        if (!active) return;
        if (!snap.exists) {
          setError('That clinic could not be found. Please check the link and try again.');
        } else {
          setClinicName(snap.data()?.title || 'this clinic');
        }
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError('Something went wrong loading this clinic. Please try again.');
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clinicId]);

  const handleCheckIn = async () => {
    setCheckingIn(true);
    setCheckInError(null);
    try {
      await submitCheckIn(clinicId);
      setSuccess(true);
    } catch (err) {
      setCheckInError(err.message);
    } finally {
      setCheckingIn(false);
    }
  };

  const renderBody = () => {
    if (loading || userLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    if (success) {
      return (
        <Stack spacing={2} alignItems="center" textAlign="center">
          <CheckCircleRounded sx={{ fontSize: 56, color: 'success.main' }} />
          <Typography variant="h6">You're checked in</Typography>
          <Typography variant="body2" color="text.secondary">
            You're checked in to {clinicName}. Any messages they send will appear
            in your Messages.
          </Typography>
          <Button component={RouterLink} to="/dashboard/messages" variant="contained">
            Go to Messages
          </Button>
        </Stack>
      );
    }

    // An anonymous guest (they scanned the VisitStart code first) is not signed
    // in for this purpose: createCheckInVisit takes the patient id from the auth
    // context, so checking in as a guest files the visit under a throwaway uid and
    // puts it in the clinic's 30-day broadcast audience, where every message sent
    // to it goes nowhere.
    if (!user || user.isAnonymous) {
      return (
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Typography variant="body1">
            Please sign in to check in to {clinicName}.
          </Typography>
          <Button component={RouterLink} to="/auth" variant="contained">
            Sign in
          </Button>
          <Typography variant="caption" color="text.secondary">
            After signing in, reopen this check-in link to finish.
          </Typography>
        </Stack>
      );
    }

    return (
      <Stack spacing={2} alignItems="center" textAlign="center">
        <Typography variant="body1">
          Check in to <strong>{clinicName}</strong> to let the clinic know you're here.
        </Typography>
        {checkInError && (
          <Alert severity="error" sx={{ width: '100%' }} onClose={() => setCheckInError(null)}>
            {checkInError}
          </Alert>
        )}
        <LoadingButton
          variant="contained"
          size="large"
          loading={checkingIn}
          onClick={handleCheckIn}
        >
          Check in
        </LoadingButton>
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 440, width: '100%' }}>
        <Stack spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <LocalHospitalRounded sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight={700} textAlign="center">
            Clinic Check-in
          </Typography>
        </Stack>
        {renderBody()}
      </Paper>
    </Box>
  );
};

export default CheckInPage;
